/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { FieldValidationError, Order, PagingRequest, QueryResult, ResourceQueryResult, Services } from '../util/services';
import { ObservableErrorResourceParser } from '../util/errors';
import { Address, AddressResource } from '../address';
import { List, Map, Set } from 'immutable';
import { CompanyLocationResource, CompanyLocationResourceService } from './company-location-resource.service';
import { EmptyMessage, IdentityMessage } from '../util/messages';
import { CompanyResource } from '../company/company-resource.service';
import { UserItem, UserItemResource } from '../user.service';
import { HistoryLog } from '../history-log/history-log.service';
import CompanyLocationHistoryType = CompanyLocation.CompanyLocationHistoryType;
/* eslint-enable */

@Injectable()
export class CompanyLocationService implements CompanyLocation.Service {

  constructor(private resourceService: CompanyLocationResourceService) {
  }

  query(request: CompanyLocation.QueryRequest): Observable<QueryResult<CompanyLocation.CompanyLocation>> {
    return Observable.create((observer: Observer<QueryResult<CompanyLocation.CompanyLocation>>) => {
      const resourceRequest: CompanyLocationResource.QueryRequest = {
        id: Services.createIdParameter(request.id),
        external_id: request.externalId,
        name: request.name,
        company_ids: Services.createIdParameter(request.companyIds),
        disabled: request.disabled,
        q: request.queryText,
        order: Services.createOrderFieldParameter(Keys.toOrderFieldKey, request.orders),
        page_number: request.paging ? request.paging.pageNumber : undefined,
        number_of_items: request.paging ? request.paging.numberOfItems : undefined,
        no_progress_bar: request.noProgressBar
      };
      return this.resourceService.query(resourceRequest).subscribe(
        (result: ResourceQueryResult<CompanyLocationResource.CompanyLocation>) => {
          observer.next({
            items: this.toPublicList(result.items),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  get(request: CompanyLocation.GetRequest): Observable<CompanyLocation.CompanyLocation> {
    return Observable.create((observer: Observer<CompanyLocation.CompanyLocation>) => {
      const resourceRequest: CompanyLocationResource.GetRequest = {
        id: request.id
      };
      return this.resourceService.get(resourceRequest).subscribe(
        (result: CompanyLocationResource.CompanyLocation) => {
          observer.next(this.toPublic(result));
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  create(request: CompanyLocation.CreateRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest: CompanyLocationResource.CreateRequest = {
        external_id: request.externalId,
        name: request.name,
        gate_crossing_required: request.gateCrossingRequired,
        company_id: request.companyId,
        place: AddressResource.Mapper.fromPublicPlace(request.place)
      };
      return this.resourceService.create(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  update(request: CompanyLocation.UpdateRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest: CompanyLocationResource.UpdateRequest = {
        id: request.id,
        external_id: request.externalId,
        name: request.name,
        gate_crossing_required: request.gateCrossingRequired,
        company_id: request.companyId,
        place: AddressResource.Mapper.fromPublicPlace(request.place)
      };
      return this.resourceService.update(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  setDisabled(request: CompanyLocation.DisableRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest: CompanyLocationResource.DisableRequest = {
        id: request.id,
        disabled: request.disabled
      };
      return this.resourceService.setDisabled(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }


  private translateError(error: any): any {
    const res = ObservableErrorResourceParser.parseError(error);
    const fieldErrors = ObservableErrorResourceParser.extractFieldErrors(res);
    const fieldErrorMap = ObservableErrorResourceParser.toFieldErrorMap(Keys.toValidatedField, fieldErrors);
    if (!fieldErrorMap.isEmpty()) {
      return FieldValidationError.of(fieldErrorMap);
    }
    return error;
  }

  private toPublicList(resourceList: CompanyLocationResource.CompanyLocation[]): List<CompanyLocation.CompanyLocation> {
    return List.of(...resourceList.map((r) => this.toPublic(r)));
  }

  private toPublic(r: CompanyLocationResource.CompanyLocation): CompanyLocation.CompanyLocation {
    return {
      id: r.id,
      externalId: r.external_id,
      name: r.name,
      gateCrossingRequired: r.gate_crossing_required,
      disabled: r.disabled,
      company: r.company,
      place: AddressResource.Mapper.toPublicPlace(r.place)
    };
  }

  history(request: CompanyLocation.HistoryRequest): Observable<QueryResult<CompanyLocation.HistoryItem>> {
    return Observable.create((observer: Observer<QueryResult<CompanyLocation.HistoryItem>>) => {
      const resourceRequest: CompanyLocationResource.HistoryRequest = {
        id: request.id,
        with_read: request.withRead,
        q: request.queryText,
        order: Services.createOrderFieldParameter(Keys.toOrderFieldKey, request.orders),
        page_number: request.paging ? request.paging.pageNumber : undefined,
        number_of_items: request.paging ? request.paging.numberOfItems : undefined,
      };
      return this.resourceService.history(resourceRequest).subscribe(
        (result: ResourceQueryResult<CompanyResource.HistoryItem>) => {
          observer.next({
            items: this.toPublicHistoryList(result.items),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });

    });
  }

  private toPublicHistoryList(resourceList: CompanyResource.HistoryItem[]): List<CompanyLocation.HistoryItem> {
    return List.of(...resourceList.map((r) => this.toPublicHistory(r)));
  }

  private toPublicHistory(r: CompanyResource.HistoryItem): CompanyLocation.HistoryItem {
    return {
      id: r.id,
      creationTime: Services.toOffsetDateTime(r.creation_time),
      type: <CompanyLocationHistoryType>r.type,
      changeLog: r.change_log,
      applicationClassification: r.application_classification,
      issuerUser: this.toPublicUser(r.issuer_user!),
      mobileApplication: this.toPublicMobileApplication(r.mobile_application)
    };
  }

  private toPublicUser(r: UserItemResource): UserItem {
    return {
      id: r.id,
      personName: r.person_name
    };
  }

  private toPublicMobileApplication(r: HistoryLog.MobileApplicationItemResource | undefined):
    HistoryLog.MobileApplicationItem | undefined {
    if (r) {
      return {
        id: r.id,
        applicationId: r.application_id
      };
    }
    return undefined;
  }
}

export namespace CompanyLocation {

  export interface Service {

    // <editor-fold desc="CRUD">

    query(request: QueryRequest): Observable<QueryResult<CompanyLocation>>;

    get(request: GetRequest): Observable<CompanyLocation>;

    create(request: CreateRequest): Observable<EmptyMessage>;

    update(request: UpdateRequest): Observable<EmptyMessage>;

    setDisabled(request: DisableRequest): Observable<EmptyMessage>;

    // </editor-fold>

  }

  export interface CompanyLocation {
    id: number;
    externalId: string;
    name: string;
    gateCrossingRequired: boolean;
    company: CompanyBaseData
    place: Address.Place;
    disabled: boolean;
  }

  export interface QueryRequest {
    id?: Set<number>;
    externalId?: string;
    name?: string;
    companyIds?: Set<number>;
    queryText?: string;
    orders?: Set<Order<OrderField>>;
    paging?: PagingRequest;
    disabled?: boolean;
    noProgressBar?: boolean;
  }

  export interface GetRequest {
    id: number;
  }

  export interface CreateRequest {
    externalId?: string;
    name: string;
    gateCrossingRequired: boolean;
    companyId: number;
    place: Address.PlaceCreateRequest;
  }

  export interface UpdateRequest {
    id: number;
    externalId: string;
    name: string;
    gateCrossingRequired: boolean;
    companyId: number;
    place: Address.PlaceCreateRequest;
  }

  export interface DisableRequest {
    id: number;
    disabled: boolean;
  }

  export interface CompanyBaseData {
    id: number;
    name: string;
  }

  export interface HistoryRequest {
    id: number;
    withRead: boolean;
    queryText?: string;
    orders?: Set<Order<OrderField>>;
    paging?: PagingRequest;
  }

  export interface HistoryItem extends HistoryLog.HistoryItemBase {
    type: CompanyLocationHistoryType;
  }

  export type CompanyLocationHistoryType =
    'READ' |
    'CREATE' |
    'UPDATE' |
    'DISABLE' |
    'ENABLE';

  export class CompanyLocationHistoryTypeObject {
    type: CompanyLocationHistoryType;
    stringKey: string;
  }

  export const companyLocationHistoryTypes: CompanyLocationHistoryTypeObject[] = [
    {type: 'READ', stringKey: 'COMPANY_LOCATION_HISTORY_TYPE_READ'},
    {type: 'CREATE', stringKey: 'COMPANY_LOCATION_HISTORY_TYPE_CREATE'},
    {type: 'UPDATE', stringKey: 'COMPANY_LOCATION_HISTORY_TYPE_UPDATE'},
    {type: 'DISABLE', stringKey: 'COMPANY_LOCATION_HISTORY_TYPE_DISABLE'},
    {type: 'ENABLE', stringKey: 'COMPANY_LOCATION_HISTORY_TYPE_ENABLE'},
  ];

  export enum OrderField {
    ID,
    EXTERNAL_ID,
    NAME,
    COMPANY,
    HISTORY_CREATION_TIME,
    HISTORY_ISSUER_USER_PERSON_NAME,
    HISTORY_MOBILE_APPLICATION_APPLICTAION_ID
  }

  export enum ValidatedField {
    UNKNOWN,

    EXTERNAL_ID,
    NAME,
  }

}


class Keys {

  private static readonly ID = 'id';
  private static readonly EXTERNAL_ID = 'external_id';
  private static readonly NAME = 'name';
  private static readonly COMPANY = 'company';
  private static readonly HISTORY_CREATION_TIME = 'creation_time';
  private static readonly HISTORY_ISSUER_USER_PERSON_NAME = 'issuer_user_person_name';
  private static readonly HISTORY_MOBILE_APPLICATION_APPLICTAION_ID = 'mobile_application_application_id';

  private static readonly orderFieldKeyMap: Map<CompanyLocation.OrderField, string> = Map.of(
    CompanyLocation.OrderField.ID, Keys.ID,
    CompanyLocation.OrderField.EXTERNAL_ID, Keys.EXTERNAL_ID,
    CompanyLocation.OrderField.NAME, Keys.NAME,
    CompanyLocation.OrderField.COMPANY, Keys.COMPANY,
    CompanyLocation.OrderField.HISTORY_CREATION_TIME, Keys.HISTORY_CREATION_TIME,
    CompanyLocation.OrderField.HISTORY_ISSUER_USER_PERSON_NAME, Keys.HISTORY_ISSUER_USER_PERSON_NAME,
    CompanyLocation.OrderField.HISTORY_MOBILE_APPLICATION_APPLICTAION_ID, Keys.HISTORY_MOBILE_APPLICATION_APPLICTAION_ID,
  );

  private static readonly keyValidatedFieldMap: Map<string, CompanyLocation.ValidatedField> = Map.of(
    CompanyLocation.ValidatedField.EXTERNAL_ID, Keys.EXTERNAL_ID,
    CompanyLocation.ValidatedField.NAME, Keys.NAME,
  );

  public static toOrderFieldKey(field: CompanyLocation.OrderField): string {
    return Keys.orderFieldKeyMap.get(field)!;
  }

  public static toValidatedField(fieldKey: string): CompanyLocation.ValidatedField {
    return Keys.keyValidatedFieldMap.get(fieldKey, CompanyLocation.ValidatedField.UNKNOWN);
  }

}
